// src/components/shared/ApplyScreenModal.jsx
// Applicant-facing modal that renders an apply screen's questions and
// collects answers. On submit, calls onSubmit({ screenResponses }).

import React, { useState } from 'react';
import { Modal } from './UI';

const ALL_TYPES = ['TEXTBOX', 'TEXTAREA', 'CHECKBOX', 'RADIO', 'DROPDOWN', 'DATE', 'LINEAR_SCALE', 'FILE_UPLOAD'];

const isEmpty = (v) => v === undefined || v === null || v === ''
  || (Array.isArray(v) && v.length === 0);

const QuestionInput = ({ q, value, onChange }) => {
  const { type, config } = q;
  if (type === 'TEXTBOX') {
    return <input className="form-input" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  }
  if (type === 'TEXTAREA') {
    return <textarea className="form-input" rows={4} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  }
  if (type === 'DATE') {
    return <input className="form-input" type="date" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  }
  if (type === 'DROPDOWN') {
    const opts = config?.options || [];
    return (
      <select className="form-input" value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">— Select —</option>
        {opts.map((o, i) => <option key={i} value={o}>{o}</option>)}
      </select>
    );
  }
  if (type === 'RADIO') {
    const opts = config?.options || [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {opts.map((o, i) => (
          <label key={i} style={{ fontSize: 13 }}>
            <input type="radio" name={q.id} checked={value === o} onChange={() => onChange(o)} /> {o}
          </label>
        ))}
      </div>
    );
  }
  if (type === 'CHECKBOX') {
    const opts = config?.options || [];
    const selected = Array.isArray(value) ? value : [];
    const toggle = (o) => {
      const next = selected.includes(o) ? selected.filter(x => x !== o) : [...selected, o];
      onChange(next);
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {opts.map((o, i) => (
          <label key={i} style={{ fontSize: 13 }}>
            <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} /> {o}
          </label>
        ))}
      </div>
    );
  }
  if (type === 'LINEAR_SCALE') {
    const min = config?.min ?? 1;
    const max = config?.max ?? 5;
    const steps = [];
    for (let i = min; i <= max; i += 1) steps.push(i);
    return (
      <div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4 }}>
          {config?.minLabel && <span style={{ fontSize: 11, color: '#777' }}>{config.minLabel}</span>}
          {steps.map(n => (
            <label key={n} style={{ fontSize: 12, textAlign: 'center' }}>
              <div>{n}</div>
              <input type="radio" name={q.id} checked={Number(value) === n} onChange={() => onChange(n)} />
            </label>
          ))}
          {config?.maxLabel && <span style={{ fontSize: 11, color: '#777' }}>{config.maxLabel}</span>}
        </div>
      </div>
    );
  }
  if (type === 'FILE_UPLOAD') {
    // File uploads from inside an apply modal would need a separate upload endpoint.
    // For v1 we collect the filename only so the employer sees that the applicant
    // attempted to upload; real file storage is a follow-up.
    return (
      <div>
        <input
          type="file"
          accept={config?.acceptedMimeTypes || undefined}
          onChange={(e) => {
            const f = e.target.files?.[0];
            onChange(f ? { fileName: f.name, fileSize: f.size, mimeType: f.type } : null);
          }}
        />
        {value?.fileName && (
          <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>
            Selected: <strong>{value.fileName}</strong>
          </div>
        )}
        <p style={{ fontSize: 11, color: '#999', marginTop: 4 }}>File upload: the filename is captured. File storage support is coming soon.</p>
      </div>
    );
  }
  return null;
};

export const ApplyScreenModal = ({ open, screen, onClose, onSubmit, submitting }) => {
  const [answers, setAnswers] = useState({});
  const [errors, setErrors]   = useState({});

  React.useEffect(() => { if (open) { setAnswers({}); setErrors({}); } }, [open]);

  if (!open || !screen) return null;
  const questions = Array.isArray(screen.questions) ? screen.questions : [];

  const setAnswer = (qid, val) => {
    setAnswers(prev => ({ ...prev, [qid]: val }));
    if (errors[qid]) setErrors(prev => { const n = { ...prev }; delete n[qid]; return n; });
  };

  const handleSubmit = () => {
    const missing = {};
    for (const q of questions) {
      if (q.required && isEmpty(answers[q.id])) missing[q.id] = 'Required';
    }
    if (Object.keys(missing).length) { setErrors(missing); return; }
    const screenResponses = questions
      .filter(q => !isEmpty(answers[q.id]))
      .map(q => ({ questionId: q.id, answer: answers[q.id] }));
    onSubmit({ screenResponses });
  };

  return (
    <Modal open={open} onClose={onClose} title="Screening questions" width="620px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit application'}
          </button>
        </div>
      }>
      {screen.intro && (
        <p style={{ fontSize: 13, color: '#444', marginTop: 0, marginBottom: 16 }}>{screen.intro}</p>
      )}
      {questions.length === 0 ? (
        <p style={{ fontSize: 13, color: '#666', margin: 0 }}>No screening questions.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {questions.map((q, i) => (
            <div key={q.id}>
              <label className="form-label">
                {i + 1}. {q.prompt}
                {q.required && <span style={{ color: '#CC0000', marginLeft: 3 }}>*</span>}
              </label>
              {ALL_TYPES.includes(q.type) ? (
                <QuestionInput q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
              ) : (
                <div style={{ color: '#999', fontSize: 12 }}>Unsupported question type: {q.type}</div>
              )}
              {errors[q.id] && <div style={{ color: '#CC0000', fontSize: 11, marginTop: 4 }}>{errors[q.id]}</div>}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
